/**
 * src/services/liveToolsService.ts
 *
 * Client-side driver for the GeoTrust Live Tools subsystem.
 *
 * Responsibilities:
 *   1. REST CRUD for tool layouts (broadcaster only — needs grant token).
 *   2. Read-only snapshot fetch for late-joining viewers.
 *   3. Local subscriber registry that the WebRTC service feeds with
 *      `live_tool_event` messages so the React layer can re-render
 *      without each component independently parsing the WS payload.
 *
 * The WS event channel is shared with the existing `webrtcBroadcastService`
 * — we don't open a separate socket.  See `webrtcBroadcastService.ts` for
 * how the `onLiveToolEvent` callback hooks back into this module.
 */

const DEFAULT_BACKEND = (
  (typeof process !== 'undefined' && (process as any).env?.REACT_APP_BACKEND_URL) || ''
) as string;

export type LiveToolType =
  | 'price_feed'
  | 'price_chart'
  | 'contract_function'
  | 'trustline'
  // Phase 3 / slice 8 — drone & airspace overlays.
  | 'drone_telemetry'
  | 'gcs_status'
  | 'airspace_status';

export type LiveToolDisplayMode =
  | 'overlay_card'
  | 'compact_badge'
  | 'pinned_sidebar'
  | 'broadcaster_background'
  | 'chart_panel'
  | 'fullscreen_focus';

export type LiveToolPosition =
  | 'top_left'
  | 'top_right'
  | 'bottom_left'
  | 'bottom_right'
  | 'center'
  | 'sidebar_left'
  | 'sidebar_right'
  | 'background';

export type LiveToolStatus = 'live' | 'stale' | 'unavailable' | 'error';

export interface PriceFeedConfig {
  symbols: string[];
  // Optional aliasing kept for the single-symbol legacy shape.
  symbol?: string;
}

export interface PriceChartConfig {
  symbol: string;
  timeframe: '1m' | '5m' | '15m' | '1h' | '4h' | '1d';
  limit?: number;
}

export interface ContractFunctionConfig {
  contractId: string;
  network: 'testnet' | 'mainnet' | string;
  functionName: string;
  parameters?: Record<string, unknown>;
  displayName?: string | null;
}

export interface TrustlineConfig {
  accountId: string;
  mode: 'all' | 'selected_asset' | 'gift_asset';
  assetCode?: string | null;
  issuer?: string | null;
}

export interface DroneTelemetryConfig {
  /** Registered entity id of the drone to surface telemetry for. */
  entityId: string;
  /**
   * Optional whitelist of telemetry fields to display. When omitted,
   * the backend's default field set is used.
   */
  fields?: string[];
}

export interface GcsStatusConfig {
  /**
   * Optional list of entity ids to surface link health for. When
   * omitted, the GCS status tool reports every entity the gateway
   * has heard from.
   */
  entityIds?: string[];
}

export interface AirspaceStatusConfig {
  lat: number;
  lng: number;
  altitudeM?: number;
  radiusM?: number;
  /**
   * Optional entity id used as a label only — the backend resolves
   * the airspace via GeoLink for the supplied static lat/lng so the
   * call rate stays predictable.
   */
  entityId?: string | null;
}

export type AnyToolConfig =
  | PriceFeedConfig
  | PriceChartConfig
  | ContractFunctionConfig
  | TrustlineConfig
  | DroneTelemetryConfig
  | GcsStatusConfig
  | AirspaceStatusConfig
  | Record<string, unknown>;

export interface LiveTool {
  id: string;
  sessionId: string;
  type: LiveToolType;
  displayName: string;
  enabled: boolean;
  visible: boolean;
  displayMode: LiveToolDisplayMode;
  position: LiveToolPosition;
  refreshIntervalSeconds: number;
  config: AnyToolConfig;
  lastValue: unknown;
  lastUpdatedAt: string | null;
  status: LiveToolStatus;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LiveToolSnapshot {
  sessionId: string;
  tools: LiveTool[];
  capturedAt: string;
}

export type LiveToolEventName =
  | 'tool.snapshot'
  | 'tool.added'
  | 'tool.updated'
  | 'tool.removed'
  | 'tool.hidden'
  | 'tool.shown'
  | 'tool.position.updated'
  | 'tool.display_mode.updated'
  | 'tool.refresh_interval.updated'
  | 'tool.value.updated'
  | 'tool.error';

export interface LiveToolEvent {
  event: LiveToolEventName;
  sessionId: string;
  toolId?: string | null;
  tool?: LiveTool | null;
  snapshot?: LiveToolSnapshot;
  error?: string;
}

// ── REST API ────────────────────────────────────────────────────────────────

function backendUrl(override?: string): string {
  return override || DEFAULT_BACKEND || (typeof window !== 'undefined' ? window.location.origin : '');
}

async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
  { backend, grantToken }: { backend?: string; grantToken?: string } = {},
): Promise<T> {
  const url = `${backendUrl(backend)}${path}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((init.headers || {}) as Record<string, string>),
  };
  if (grantToken) headers['X-Live-Session-Grant'] = grantToken;
  const resp = await fetch(url, { ...init, headers });
  const text = await resp.text();
  let json: any = null;
  try { json = text ? JSON.parse(text) : {}; } catch { json = { raw: text }; }
  if (!resp.ok) {
    const err = new Error(json?.error || `HTTP ${resp.status}`);
    (err as any).status = resp.status;
    (err as any).body = json;
    throw err;
  }
  return json as T;
}

export async function getCapabilities(opts: { backend?: string } = {}): Promise<any> {
  return apiFetch('/api/live-tools/capabilities', {}, opts);
}

export async function getSnapshot(
  sessionId: string,
  opts: { backend?: string } = {},
): Promise<LiveToolSnapshot> {
  const out = await apiFetch<{ ok: boolean; snapshot: LiveToolSnapshot }>(
    `/api/live-tools/sessions/${encodeURIComponent(sessionId)}/snapshot`,
    {},
    opts,
  );
  return out.snapshot;
}

export async function listTools(
  sessionId: string,
  opts: { backend?: string } = {},
): Promise<LiveTool[]> {
  const out = await apiFetch<{ ok: boolean; tools: LiveTool[] }>(
    `/api/live-tools/sessions/${encodeURIComponent(sessionId)}/tools`,
    {},
    opts,
  );
  return out.tools;
}

export interface AddToolInput {
  type: LiveToolType;
  displayName?: string;
  config: AnyToolConfig;
  enabled?: boolean;
  visible?: boolean;
  displayMode?: LiveToolDisplayMode;
  position?: LiveToolPosition;
  refreshIntervalSeconds?: number;
}

export async function addTool(
  sessionId: string,
  input: AddToolInput,
  opts: { backend?: string; grantToken: string },
): Promise<LiveTool> {
  const out = await apiFetch<{ ok: boolean; tool: LiveTool }>(
    `/api/live-tools/sessions/${encodeURIComponent(sessionId)}/tools`,
    { method: 'POST', body: JSON.stringify(input) },
    opts,
  );
  return out.tool;
}

export async function updateTool(
  sessionId: string,
  toolId: string,
  patch: Partial<AddToolInput> & { config?: Partial<AnyToolConfig> },
  opts: { backend?: string; grantToken: string },
): Promise<LiveTool> {
  const out = await apiFetch<{ ok: boolean; tool: LiveTool }>(
    `/api/live-tools/sessions/${encodeURIComponent(sessionId)}/tools/${encodeURIComponent(toolId)}`,
    { method: 'PATCH', body: JSON.stringify(patch) },
    opts,
  );
  return out.tool;
}

export async function removeTool(
  sessionId: string,
  toolId: string,
  opts: { backend?: string; grantToken: string },
): Promise<boolean> {
  await apiFetch<{ ok: boolean }>(
    `/api/live-tools/sessions/${encodeURIComponent(sessionId)}/tools/${encodeURIComponent(toolId)}`,
    { method: 'DELETE' },
    opts,
  );
  return true;
}

export async function setVisibility(
  sessionId: string,
  toolId: string,
  visible: boolean,
  opts: { backend?: string; grantToken: string },
): Promise<LiveTool> {
  const out = await apiFetch<{ ok: boolean; tool: LiveTool }>(
    `/api/live-tools/sessions/${encodeURIComponent(sessionId)}/tools/${encodeURIComponent(toolId)}/visibility`,
    { method: 'POST', body: JSON.stringify({ visible }) },
    opts,
  );
  return out.tool;
}

export async function refreshTool(
  sessionId: string,
  toolId: string,
  opts: { backend?: string; grantToken: string },
): Promise<LiveTool> {
  const out = await apiFetch<{ ok: boolean; tool: LiveTool }>(
    `/api/live-tools/sessions/${encodeURIComponent(sessionId)}/tools/${encodeURIComponent(toolId)}/refresh`,
    { method: 'POST' },
    opts,
  );
  return out.tool;
}

export interface RegisterContractInput {
  contractId: string;
  network: 'testnet' | 'mainnet' | string;
  name?: string;
  /** Discovered functions array from GeoLink's /api/contracts/discover. */
  discoveredFunctions: Array<{
    name: string;
    parameters?: any[];
    returns?: string;
    [k: string]: unknown;
  }>;
}

export async function registerContract(
  input: RegisterContractInput,
  opts: { backend?: string } = {},
): Promise<any> {
  const out = await apiFetch<{ ok: boolean; contract: any }>(
    `/api/live-tools/registered-contracts`,
    { method: 'POST', body: JSON.stringify(input) },
    opts,
  );
  return out.contract;
}

export async function listRegisteredContracts(opts: { backend?: string } = {}): Promise<any[]> {
  const out = await apiFetch<{ ok: boolean; contracts: any[] }>(
    `/api/live-tools/registered-contracts`,
    {},
    opts,
  );
  return out.contracts;
}

/**
 * Ask the GeoTrust backend to resolve a contract's metadata via
 * GeoLink's `/api/contracts/by-address` and cache it in the local
 * registry.  Use this in the broadcaster UI to populate a contract
 * picker without manually copying discovered_functions.
 */
export async function syncContractFromGeoLink(
  contractId: string,
  network: 'testnet' | 'mainnet' | string = 'testnet',
  opts: { backend?: string } = {},
): Promise<any> {
  const out = await apiFetch<{ ok: boolean; contract: any }>(
    `/api/live-tools/registered-contracts/sync-from-geolink`,
    { method: 'POST', body: JSON.stringify({ contractId, network }) },
    opts,
  );
  return out.contract;
}

// ── Local event bus ─────────────────────────────────────────────────────────

type Subscriber = (event: LiveToolEvent) => void;
const _subscribers = new Set<Subscriber>();

/**
 * Subscribe to live tool events.  Returns an unsubscribe function.
 * The events are delivered by `webrtcBroadcastService` when it
 * receives `live_tool_event` messages from the signaling hub.
 */
export function subscribeToLiveToolEvents(fn: Subscriber): () => void {
  _subscribers.add(fn);
  return () => { _subscribers.delete(fn); };
}

/**
 * Internal — invoked by `webrtcBroadcastService` (broadcaster +
 * viewer paths) whenever the hub pushes a `live_tool_event` message.
 * Exported so the WebRTC service can dispatch without the React tree
 * needing to import this module first.
 */
export function dispatchLiveToolEvent(event: LiveToolEvent): void {
  // Snapshot to an Array first so a) we don't need --downlevelIteration
  // on Set, and b) a subscriber that unsubscribes itself mid-dispatch
  // doesn't perturb the iteration order.
  _subscribers.forEach((fn) => {
    try { fn(event); } catch { /* never let one subscriber crash others */ }
  });
}

// ── Reducer helpers (for React state) ───────────────────────────────────────

/**
 * Apply a `LiveToolEvent` to a tool list, returning the new list.  The
 * function is pure — call sites can wire it through `useReducer` or
 * `useState` without any extra plumbing.  Order is preserved by `id`
 * to avoid layout flicker.
 */
export function applyLiveToolEvent(prev: LiveTool[], ev: LiveToolEvent): LiveTool[] {
  switch (ev.event) {
    case 'tool.snapshot':
      return Array.isArray(ev.snapshot?.tools) ? ev.snapshot!.tools.slice() : [];
    case 'tool.added': {
      if (!ev.tool) return prev;
      if (prev.some((t) => t.id === ev.tool!.id)) return prev;
      return [...prev, ev.tool];
    }
    case 'tool.removed': {
      if (!ev.toolId) return prev;
      return prev.filter((t) => t.id !== ev.toolId);
    }
    case 'tool.updated':
    case 'tool.hidden':
    case 'tool.shown':
    case 'tool.position.updated':
    case 'tool.display_mode.updated':
    case 'tool.refresh_interval.updated':
    case 'tool.value.updated':
    case 'tool.error': {
      if (!ev.tool) return prev;
      return prev.map((t) => (t.id === ev.tool!.id ? { ...t, ...ev.tool! } : t));
    }
    default:
      return prev;
  }
}
